import { sendGAEvent } from '../utils';

export enum DepositEventsEnum {
  DEP_SUCCESS = 'dep_success',
  DEPOSIT_APPEARENCE = 'deposit_appearance',
  DEPOSIT_BONUS_CLICK = 'deposit_bonus_click',
  DEPOSIT_PROMO_CODE_ACTIVATE = 'deposit_promo_code_activate',
  DEPOSIT_BUTTON_STEP_1 = 'deposit_button_step_1',
  DEPOSIT_PAYMENT_METHOD_CLICK = 'deposit_payment_method_click',
  DEPOSIT_BUTTON_STEP_2 = 'deposit_button_step_2',
  DEPOSIT_CLOSE = 'deposit_close',
  DEPOSIT_PAYMENT_METHOD_CRASHED = 'deposit_payment_method_crashed',
  DEPOSIT_PAYMENT_METHODS_FAILED_LOADING = 'deposit_payment_methods_failed_loading',
  DEPOSIT_PAYMENT_METHODS_SUBMIT_ERROR = 'deposit_payment_methods_submit_error',
}

type depositModalType = 'slots' | 'sports';

export interface IDepositSuccessData {
  type: 'ftd' | 'std';
  amount: number | string;
  currency: string;
}

export interface IDepositAppearenceData {
  dep_modal_type: depositModalType;
  amount?: number | string;
  currency: string;
}

export interface IDepositBonusClick {
  dep_modal_type: depositModalType;
  type: 'on' | 'off' | 'hint';
  id: string | number;
}

interface IDepositPromoCodeActivate {
  dep_modal_type: depositModalType;
  code: string;
  code_sent_to_back: string;
  status: 'success' | 'failed';
  error_data?: string;
}

interface IDepositPaymentMethodClick {
  dep_modal_type: depositModalType;
  payment_brand: string;
  payment_provider: string;
  payment_method_id: string;
  payment_type: string;
}

interface IDepositClose {
  dep_modal_type: depositModalType;
}

interface IDepositButtonStep2 {
  dep_modal_type: depositModalType;
  amount: number | string;
  currency: string;
  button_text: string;
  request_data: string;
  error_data: string;
  payment_brand: string;
  payment_provider: string;
  payment_method_id: string;
  payment_type: string;
}

interface IPaymentMethodCrashed {
  payment_method_id: string;
  field_title: string;
  field_name: string;
  field_type: string;
  error?: string;
}

interface IPaymentMethodsSubmitError {
  submit_error: string;
}

interface IDepositPaymentMethodsFailedLoading {
  error?: string;
}

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when a deposit is successful.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `dep_success` and the category is `deposit`.
 * The data object contains the type of deposit (`ftd` or `std`), amount, and currency.
 */
export const sendDepositSuccessEvent = (data: IDepositSuccessData) => {
  sendGAEvent(DepositEventsEnum.DEP_SUCCESS, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when a deposit modal is shown.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_appearance` and the category is `deposit`.
 * The data object contains the deposit modal type, amount, and currency.
 */
export const sendDepositAppearanceEvent = (data: IDepositAppearenceData) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_APPEARENCE, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user click on some bonus card/hint
 * on step 1 inside deposit modal.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_bonus_click` and the category is `deposit`.
 * The data object contains the deposit modal type, bonus type (`on`, `off`, or `hint`), and bonus id.
 */
export const sendDepositBonusClickEvent = (data: IDepositBonusClick) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_BONUS_CLICK, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user click on promocode
 * activate button inside deposit modal, on promocode tab.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_promo_code_activate` and the category is `deposit`.
 * The data object contains the deposit modal type, promocode, status, and optional error data.
 */
export const sendDepositPromoCodeActivateEvent = (
  data: IDepositPromoCodeActivate
) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_PROMO_CODE_ACTIVATE, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user click on some payment
 * method on step 2 inside deposit modal.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_payment_method_click` and the category is `deposit`.
 * The data object contains the deposit modal type and payment method.
 */
export const sendDepositPaymentMethodClickEvent = (
  data: IDepositPaymentMethodClick
) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_PAYMENT_METHOD_CLICK, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user close the deposit modal.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_close` and the category is `deposit`.
 * The data object contains the deposit modal type.
 */
export const sendDeposiCloseEvent = (data: IDepositClose) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_CLOSE, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when a payment method crashed the deposit modal.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_payment_method_crashed` and the category is `deposit`.
 * The data object contains the deposit modal type.
 */
export const sendDepositPaymentMethodCrashedEvent = (
  data: IPaymentMethodCrashed
) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_PAYMENT_METHOD_CRASHED, {
    event_category: 'deposit',
    ...data,
  });
};

/**
 *
 * @description This function sends a Google Analytics event when a payment method not loaded in deposit modal.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_payment_methods_failed_loading` and the category is `deposit`.
 * The data object contains the deposit modal type.
 */
export const sendDepositPaymentMethodsFailedLoading = (
  data?: IDepositPaymentMethodsFailedLoading
) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_PAYMENT_METHODS_FAILED_LOADING, {
    event_category: 'deposit',
    error: data?.error,
  });
};

/**
 *
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when a payment method submit returns an error.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `deposit_payment_methods_submit_error` and the category is `deposit`.
 * The data object contains the deposit modal type.
 */
export const sendDepositPaymentSubmitError = (
  data: IPaymentMethodsSubmitError
) => {
  sendGAEvent(DepositEventsEnum.DEPOSIT_PAYMENT_METHODS_SUBMIT_ERROR, {
    event_category: 'deposit',
    ...data,
  });
};
